import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useCartContext } from "../context/CartContext";
import { toast } from "react-toastify";
import {
  ShoppingCart,
  Heart,
  Share2,
  Star,
  Package,
  Truck,
  Shield,
  Leaf,
  Zap,
  CheckCircle,
  XCircle,
  RefreshCw,
  ArrowLeft,
  ZoomIn,
  Award,
  TrendingUp,
  ChevronRight,
  Plus,
  Minus,
  AlertCircle
} from "lucide-react";

// Enhanced component to display a product image with zoom
const ProductImage = ({ src, alt, className }) => {
  const [showZoom, setShowZoom] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [isFavorited, setIsFavorited] = useState(false);
  const containerRef = useRef(null);
  const zoomLevel = 2.5; // Niveau de zoom (2.5x)

  const getImageUrl = (imagePath) => {
    if (!imagePath) return "/placeholder-image.jpg";

    if (
      imagePath.startsWith("data:image") ||
      imagePath.startsWith("http://") ||
      imagePath.startsWith("https://")
    ) {
      return imagePath; // Already a complete link or base64
    }

    const API_URL = "https://api.greenecobagsusa.com"; // Corrected URL without trailing slash
    return `${API_URL}/storage/${imagePath}`;
  };

  const handleMouseMove = (e) => {
    if (!containerRef.current) return;

    // Calculate relative mouse position in the container
    const rect = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setPosition({ x, y });
  };

  const handleMouseEnter = () => setShowZoom(true);
  const handleMouseLeave = () => setShowZoom(false);

  const handleImageLoad = () => setIsLoading(false);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: alt || "Product Image",
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard!");
    }
  };

  return (
    <div className="relative group">
      {/* Main Image Container */}
      <div
        className="relative overflow-hidden rounded-3xl shadow-2xl bg-gradient-to-br from-gray-50 to-gray-100 border border-white/20"
        style={{ height: '500px' }}
        ref={containerRef}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Loading State */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 z-20">
            <div className="flex flex-col items-center space-y-4">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center animate-pulse">
                <Package className="w-8 h-8 text-white" />
              </div>
              <div className="text-gray-600 font-medium">Loading image...</div>
            </div>
          </div>
        )}

        {/* Main Image */}
        <img
          src={getImageUrl(src)}
          alt={alt || "Product Image"}
          className={`w-full h-full object-cover transition-all duration-500 ${isLoading ? 'opacity-0' : 'opacity-100'} ${className}`}
          onLoad={handleImageLoad}
          onError={(e) => {
            console.error("Image loading error:", src);
            e.target.src = "/placeholder-image.jpg";
            e.target.onerror = null;
            setIsLoading(false);
          }}
        />

        {/* Zoom Overlay */}
        {showZoom && !isLoading && (
          <div
            className="absolute top-0 left-0 w-full h-full pointer-events-none bg-white/90 backdrop-blur-sm"
            style={{
              backgroundImage: `url(${getImageUrl(src)})`,
              backgroundPosition: `${position.x}% ${position.y}%`,
              backgroundSize: `${zoomLevel * 100}%`,
              backgroundRepeat: 'no-repeat',
              zIndex: 10,
              transition: 'background-position 0.1s ease-out'
            }}
          />
        )}

        {/* Action Buttons */}
        <div className="absolute top-4 right-4 flex flex-col space-y-3 z-30">
          {/* Favorite Button */}
          <button
            onClick={() => setIsFavorited(!isFavorited)}
            className={`w-12 h-12 rounded-2xl backdrop-blur-lg border border-white/20 flex items-center justify-center transition-all duration-300 transform hover:scale-110 ${isFavorited
              ? 'bg-red-500/90 text-white shadow-lg'
              : 'bg-white/80 text-gray-600 hover:bg-white/90'
              }`}
          >
            <Heart className={`w-5 h-5 ${isFavorited ? 'fill-current' : ''}`} />
          </button>

          {/* Share Button */}
          <button
            onClick={handleShare}
            className="w-12 h-12 rounded-2xl bg-white/80 backdrop-blur-lg border border-white/20 text-gray-600 hover:bg-white/90 hover:text-gray-800 flex items-center justify-center transition-all duration-300 transform hover:scale-110"
          >
            <Share2 className="w-5 h-5" />
          </button>
        </div>

        {/* Zoom Indicator */}
        {showZoom && !isLoading && (
          <div className="absolute bottom-4 left-4 bg-black/70 text-white px-3 py-2 rounded-xl backdrop-blur-lg border border-white/20 z-30">
            <div className="flex items-center space-x-2">
              <ZoomIn className="w-4 h-4" />
              <span className="text-sm font-medium">Zoom Active</span>
            </div>
          </div>
        )}

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
      </div>

      {/* Image Quality Badge */}
      <div className="absolute bottom-4 right-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-3 py-2 rounded-xl shadow-lg z-30">
        <div className="flex items-center space-x-2">
          <Award className="w-4 h-4" />
          <span className="text-sm font-medium">HD Quality</span>
        </div>
      </div>
    </div>
  );
};

// Main component to display product details
const ProductDetails = () => {
  const { id } = useParams();
  const { addToCart } = useCartContext();
  const [product, setProduct] = useState(null);
  const [sizes, setSizes] = useState([]);
  const [selectedSize, setSelectedSize] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  // API URL corrected with HTTPS protocol
  const API_URL = "https://api.greenecobagsusa.com/api";

  // Helper function to format price by removing trailing zeros
  const formatPrice = (price) => {
    // Convert to string first
    const priceStr = price.toString();

    // If it's not a valid number, return as is
    if (isNaN(parseFloat(priceStr))) {
      return priceStr;
    }

    // Parse as number to remove trailing zeros, then convert back to string
    const numPrice = parseFloat(priceStr);

    // If it's a whole number, add .00
    if (numPrice % 1 === 0) {
      return numPrice.toString() + '.00';
    }

    // For decimal numbers, parseFloat automatically removes trailing zeros
    return numPrice.toString();
  };

  useEffect(() => {
    const fetchProductAndSizes = async () => {
      setLoading(true);
      try {
        const [productResponse, sizesResponse] = await Promise.all([
          fetch(`${API_URL}/products/${id}`),
          fetch(`${API_URL}/sizes`)
        ]);

        if (!productResponse.ok || !sizesResponse.ok) {
          throw new Error(
            !productResponse.ok
              ? `Product error: ${productResponse.status}`
              : `Sizes error: ${sizesResponse.status}`
          );
        }

        const productJson = await productResponse.json();
        const sizesJson = await sizesResponse.json();

        // Ensure we use product data correctly
        const productData = productJson.data;

        // Check if data is available
        if (!productData) {
          throw new Error("Product data not found");
        }

        // For sizes, check data structure
        const sizesData = Array.isArray(sizesJson)
          ? sizesJson
          : (sizesJson.data || []);

        setProduct(productData);
        setSizes(sizesData);

        if (sizesData.length > 0) {
          setSelectedSize(sizesData[0]);
        }

        setError(null);
        setIsVisible(true);
      } catch (err) {
        console.error("Error fetching product/sizes:", err);
        setError(err.message || "Error loading product.");
      } finally {
        setLoading(false);
      }
    };

    fetchProductAndSizes();
  }, [id, API_URL]);

  // Option to use base64 image if available
  const getProductImage = () => {
    if (product?.image_base64) {
      return product.image_base64;
    }
    return product?.image_details || "";
  };

  const handleAddToCart = async () => {
    if (!selectedSize) {
      toast.error("Please select a size before adding to cart.");
      return;
    }

    setIsAddingToCart(true);

    try {
      // Simulate API call delay for better UX
      await new Promise(resolve => setTimeout(resolve, 1000));

      addToCart({
        ...product,
        selectedSize: selectedSize,
        price: selectedSize.price,
        quantity: quantity
      });

      toast.success(`${product.name} (${selectedSize.name}) added to cart!`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: true
      });
    } catch (error) {
      toast.error("Failed to add item to cart. Please try again.");
    } finally {
      setIsAddingToCart(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex items-center justify-center p-4">
        <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-12 max-w-md w-full text-center">
          <div className="flex flex-col items-center space-y-6">
            <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-600 rounded-3xl flex items-center justify-center animate-pulse">
              <Package className="w-10 h-10 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Loading Product</h3>
              <p className="text-gray-600">Please wait while we fetch the product details...</p>
            </div>
            <div className="flex space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-bounce"></div>
              <div className="w-3 h-3 bg-emerald-500 rounded-full animate-bounce animation-delay-200"></div>
              <div className="w-3 h-3 bg-teal-500 rounded-full animate-bounce animation-delay-400"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-pink-50 to-orange-50 flex items-center justify-center p-4">
        <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-12 max-w-md w-full text-center">
          <div className="flex flex-col items-center space-y-6">
            <div className="w-20 h-20 bg-gradient-to-r from-red-500 to-pink-600 rounded-3xl flex items-center justify-center">
              <AlertCircle className="w-10 h-10 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {error || "Product Not Found"}
              </h3>
              <p className="text-gray-600">We couldn't find the product you're looking for.</p>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="group relative bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-8 py-3 rounded-2xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <div className="flex items-center space-x-2">
                <RefreshCw className="w-5 h-5 group-hover:rotate-180 transition-transform duration-300" />
                <span>Try Again</span>
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check if properties like rating, available, and sold exist
  const rating = product.rating || "N/A";
  const available = product.dispo || "N/A";
  const sold = product.sold || 0;
  // const logoPrice = product.logo_price || 0;

  return (
    <div className={`min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 transition-all duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      {/* Background Elements */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-20 right-20 w-32 h-32 bg-green-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 left-20 w-24 h-24 bg-emerald-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-teal-200 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse animation-delay-4000"></div>
      </div>

      <div className="p-4 md:p-8 pt-24">
        <div className="max-w-7xl mx-auto">
          {/* Back Button */}
          <div className="mb-8">
            <button
              onClick={() => window.history.back()}
              className="group flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors duration-300"
            >
              <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-300" />
              <span className="font-medium">Back to Products</span>
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Image Section */}
            <div className="flex-1">
              <ProductImage
                src={getProductImage()}
                alt={product.name}
                className="w-full h-full"
              />
            </div>

            {/* Product Information Section */}
            <div className="space-y-8">
              {/* Price Section */}
              <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-xl border border-white/20 p-8">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center">
                      <Package className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-4xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                        ${formatPrice(selectedSize?.price ?? "0")}
                      </h2>
                      <p className="text-sm text-gray-600">per bag</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl px-4 py-2">
                      <p className="text-sm font-medium text-blue-700">
                        Logo: $0.20 per color
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Product Title and Rating */}
              <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-xl border border-white/20 p-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-4">{product.name}</h1>

                {(rating !== "N/A" || available !== "N/A" || sold > 0) && (
                  <div className="flex items-center space-x-6 mb-6">
                    {rating !== "N/A" && (
                      <div className="flex items-center space-x-2">
                        <div className="flex text-yellow-400">
                          {Array.from({ length: 5 }, (_, i) => (
                            <Star key={i} className={`w-5 h-5 ${i < Math.round(parseFloat(rating)) ? 'fill-current' : ''}`} />
                          ))}
                        </div>
                        <span className="font-semibold text-gray-700">{rating}</span>
                      </div>
                    )}
                    {available !== "N/A" && (
                      <div className="flex items-center space-x-2 text-green-600">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">{available} available</span>
                      </div>
                    )}
                    {sold > 0 && (
                      <div className="flex items-center space-x-2 text-gray-500">
                        <TrendingUp className="w-5 h-5" />
                        <span className="font-medium">+{sold} sold</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                    <Leaf className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-gray-700 leading-relaxed">{product.description}</p>
                </div>
              </div>

              {/* Size Selection */}
              <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-xl border border-white/20 p-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl flex items-center justify-center">
                      <Package className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Select Size</h3>
                  </div>
                  {selectedSize && (
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl px-4 py-2">
                      <span className="text-sm font-semibold text-green-700">
                        {selectedSize.label}
                      </span>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {sizes.length === 0 ? (
                    <div className="col-span-full text-center py-8">
                      <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                      <p className="text-red-600 font-medium">No sizes available</p>
                    </div>
                  ) : (
                    sizes.map((size) => (
                      <button
                        key={size.id}
                        onClick={() => setSelectedSize(size)}
                        className={`group relative p-4 rounded-2xl border-2 transition-all duration-300 transform hover:scale-105 ${selectedSize?.id === size.id
                          ? "border-green-500 bg-gradient-to-r from-green-50 to-emerald-50 shadow-lg"
                          : "border-gray-200 hover:border-green-300 bg-white hover:bg-gray-50"
                          }`}
                      >
                        <div className="text-center">
                          <div className={`font-bold text-lg mb-1 ${selectedSize?.id === size.id ? "text-green-700" : "text-gray-700"
                            }`}>
                            {size.name}
                          </div>
                          <div className={`text-sm ${selectedSize?.id === size.id ? "text-green-600" : "text-gray-500"
                            }`}>
                            ${formatPrice(size.price)}
                          </div>
                        </div>
                        {selectedSize?.id === size.id && (
                          <div className="absolute top-2 right-2">
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          </div>
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* Quantity and Add to Cart */}
              <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-xl border border-white/20 p-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-600 rounded-xl flex items-center justify-center">
                      <ShoppingCart className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Quantity</h3>
                  </div>
                </div>

                <div className="flex items-center space-x-4 mb-8">
                  <div className="flex items-center bg-gray-100 rounded-2xl p-2">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-10 h-10 rounded-xl bg-white hover:bg-gray-50 flex items-center justify-center transition-colors duration-300"
                    >
                      <Minus className="w-5 h-5 text-gray-600" />
                    </button>
                    <span className="mx-4 text-xl font-bold text-gray-900 min-w-[3rem] text-center">
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-10 h-10 rounded-xl bg-white hover:bg-gray-50 flex items-center justify-center transition-colors duration-300"
                    >
                      <Plus className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                  <div className="text-sm text-gray-600">
                    Total: <span className="font-bold text-lg text-green-600">
                      ${formatPrice((parseFloat(selectedSize?.price || 0) * quantity).toString())}
                    </span>
                  </div>
                </div>

                <button
                  onClick={handleAddToCart}
                  disabled={!selectedSize || isAddingToCart}
                  className={`group relative w-full py-4 px-8 rounded-2xl font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl ${selectedSize && !isAddingToCart
                    ? "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                    }`}
                >
                  {isAddingToCart ? (
                    <div className="flex items-center justify-center space-x-3">
                      <RefreshCw className="w-6 h-6 animate-spin" />
                      <span>Adding to Cart...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-3">
                      <ShoppingCart className="w-6 h-6" />
                      <span>{selectedSize ? "Add to Cart" : "Select a Size"}</span>
                      <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                    </div>
                  )}
                </button>
              </div>

              {/* Features Section */}
              <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-xl border border-white/20 p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <span>Why Choose Us</span>
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Leaf className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Eco-Friendly</h4>
                      <p className="text-sm text-gray-600">100% biodegradable materials</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Truck className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Fast Shipping</h4>
                      <p className="text-sm text-gray-600">Free delivery on orders over $50</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Award className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Quality Guarantee</h4>
                      <p className="text-sm text-gray-600">Premium quality materials</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Zap className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Custom Design</h4>
                      <p className="text-sm text-gray-600">Personalized logos available</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default ProductDetails;